import { captureException, withScope } from "@sentry/react";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import type {
	RenderSource,
	RenderRunner,
	LoadedScene,
} from "./render-runner-type.ts";
import type {
	RenderWorkerIncomingData,
	RenderWorkerOutgoingData,
} from "./render-worker-data.ts";
import { deserializeError } from "../utils/worker-error.ts";
import { createWorkerSupervisor } from "../utils/worker-supervisor.ts";
import type { FaceBoundingBox } from "@brickme/project-core/src/model/project.ts";

// Interesting observation, main thread rendering was faster in one test i did. 2.5ms
// compared to 7-14. Pretty small example though. I'm keeping in worker for better ui
// responsiveness. Test again when have a larger project

function createRenderWorkerBridge(): RenderRunner<HTMLCanvasElement> {
	// The render worker runs Canvas 2D with `alpha: false`, so a swallowed render
	// failure shows on screen as a blank/black canvas with nothing logged (Sentry
	// only runs on the main thread). Surface anything the worker forwards.
	function onMessage(e: MessageEvent) {
		const data = e.data as RenderWorkerOutgoingData;
		if (data.type === "render-error") {
			const error = deserializeError(data.error);
			// eslint-disable-next-line no-console
			console.error(`Render worker error (${data.context})`, error);
			withScope((scope) => {
				scope.setTag("source", "render-worker");
				scope.setExtra("renderWorkerErrorContext", data.context);
				captureException(error);
			});
		}
	}

	const supervisor = createWorkerSupervisor({
		source: "render-worker",
		label: "Render worker",
		errorContextExtra: "renderWorkerErrorContext",
		// Note: neither format of worker works locally for me atm. Either in a built preview or
		// in dev mode. Haven't tried an isolated hello world. It's working fine on dev though.
		createWorker: () =>
			new Worker(new URL("./render-worker.ts", import.meta.url), {
				type: "module",
			}),
		onMessage,
		// Not recreated: the canvases below are transferred to the worker and a
		// transferred canvas cannot be handed to a replacement. Recovery needs the
		// owning component to remount its canvas.
		startRetryDelaysMs: [],
	});

	function post(data: RenderWorkerIncomingData, transfer: Transferable[]) {
		supervisor.postMessage(data, transfer);
	}

	return {
		setSourceImage(sourceImage: Bitmap, utilityCanvas: HTMLCanvasElement) {
			const offscreenCanvas = utilityCanvas.transferControlToOffscreen();
			post(
				{
					type: "set-source-image",
					utilityCanvas: offscreenCanvas,
					sourceImage,
				},
				[offscreenCanvas],
			);
		},
		setPictureConfiguration(picture: PictureConfiguration) {
			post({ type: "set-picture", picture }, []);
		},
		setCanvas(canvas: HTMLCanvasElement) {
			const offscreenCanvas = canvas.transferControlToOffscreen();
			post({ type: "set-canvas", canvas: offscreenCanvas }, [offscreenCanvas]);
		},
		setSource(newSource: RenderSource | undefined) {
			post({ type: "set-source", source: newSource }, []);
		},
		setFaces(
			newFaces:
				| readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
				| undefined,
		) {
			post({ type: "set-faces", faces: newFaces }, []);
		},
		setDrawBaseplates(newDrawBaseplates: boolean) {
			post(
				{ type: "set-draw-baseplates", drawBaseplates: newDrawBaseplates },
				[],
			);
		},
		setDrawDebugFaces(newDrawDebugFaces: boolean) {
			post(
				{ type: "set-draw-debug-faces", drawDebugFaces: newDrawDebugFaces },
				[],
			);
		},
		setLoadedScene(loadedScene: LoadedScene | undefined) {
			post({ type: "set-loaded-scene", loadedScene }, []);
		},
		setUtilityCanvas(
			newUtilityCanvas: HTMLCanvasElement,
			canvasSize: GenericSize<number>,
		) {
			const utilityCanvas = newUtilityCanvas.transferControlToOffscreen();
			post({ type: "set-utility-canvas", utilityCanvas, canvasSize }, [
				utilityCanvas,
			]);
		},
		clearUtilityCanvas() {
			post({ type: "clear-utility-canvas" }, []);
		},
		terminate() {
			supervisor.terminate();
		},
	};
}

export { createRenderWorkerBridge };
